import { useState, useEffect, useRef, useCallback } from 'react'
import { motion } from 'framer-motion'
import {
  Search,
  LayoutDashboard,
  LogIn,
  Star,
  Globe,
  MapPin,
  ArrowRight,
  CheckCircle,
  Loader2,
  MessageSquare,
  Mail,
  Phone,
  Calendar,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { APP_NAME, BRAND, CONSULTATION_URL, FOOTER } from '../config'
import { useI18nNavigate } from '../hooks/useI18nNavigate'
import { LanguageSwitcher } from '../components/LanguageSwitcher'
import { businessApi } from '../api/client'
import { useAuthStore } from '../stores/authStore'
import logoHeader from '../assets/landing/logo-header.svg'
import logoFooter from '../assets/landing/logo-footer.svg'

// ─── Social icons (footer) ────────────────────────────────────────────────────
function SocialIcon({ name }: { name: 'instagram' | 'facebook' | 'youtube' }) {
  const paths: Record<'instagram' | 'facebook' | 'youtube', React.ReactNode> = {
    instagram: (
      <path
        fill="currentColor"
        d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"
      />
    ),
    facebook: (
      <path
        fill="currentColor"
        d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"
      />
    ),
    youtube: (
      <path
        fill="currentColor"
        d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"
      />
    ),
  }

  return (
    <svg viewBox="0 0 24 24" className="w-6 h-6" aria-hidden>
      {paths[name]}
    </svg>
  )
}
interface PlacePrediction {
  placeId: string
  mainText: string
  secondaryText: string
  /** Resolved city, available when the prediction comes from the backend search. */
  city?: string
}

function extractCityFromSecondary(secondary: string): string {
  const parts = secondary.split(', ')
  if (parts.length === 0) return ''
  if (parts.length <= 2) return parts[0]
  const localityParts = parts.slice(0, -2)
  return localityParts[localityParts.length - 1] ?? parts[0]
}

// ─── Cartley Logo ─────────────────────────────────────────────────────────────
function CartleyLogo({ variant = 'header' }: { variant?: 'header' | 'footer' }) {
  const src = variant === 'footer' ? logoFooter : logoHeader
  const height = variant === 'footer' ? 'h-[61px]' : 'h-11 sm:h-14 md:h-[62px]'

  return <img src={src} alt={APP_NAME} className={`${height} w-auto`} />
}

// ─── Component ────────────────────────────────────────────────────────────────

export function LandingPage() {
  const navigate = useI18nNavigate()
  const { t } = useTranslation('landing')
  const { t: tCommon } = useTranslation('common')
  // When a customer session exists, the "Log in" link becomes "Dashboard".
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const [query, setQuery] = useState('')
  const [city, setCity] = useState('')
  const [selectedPlaceId, setSelectedPlaceId] = useState('')

  const [predictions, setPredictions] = useState<PlacePrediction[]>([])
  const [showDropdown, setShowDropdown] = useState(false)
  const [acLoading, setAcLoading] = useState(false)

  const autoSvcRef = useRef<GoogleAutocompleteService | null>(null)
  const sessionTokRef = useRef<unknown>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const formRef = useRef<HTMLFormElement>(null)
  const reqSeqRef = useRef(0)

  useEffect(() => {
    const apiKey = import.meta.env.VITE_GOOGLE_PLACES_API_KEY
    if (!apiKey) return

    const initService = () => {
      const g = window.google
      if (!g?.maps?.places?.AutocompleteService) return
      autoSvcRef.current = new g.maps.places.AutocompleteService()
      sessionTokRef.current = new g.maps.places.AutocompleteSessionToken()
    }

    if (window.google?.maps?.places?.AutocompleteService) {
      initService()
      return
    }

    if (document.getElementById('__lg_gm')) {
      const poll = setInterval(() => {
        if (window.google?.maps?.places?.AutocompleteService) {
          clearInterval(poll)
          initService()
        }
      }, 100)
      return () => clearInterval(poll)
    }

    window.__lg_gm_cb = initService
    const script = document.createElement('script')
    script.id = '__lg_gm'
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&callback=__lg_gm_cb`
    script.async = true
    document.head.appendChild(script)

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [])

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (formRef.current && !formRef.current.contains(e.target as Node)) {
        setShowDropdown(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const refreshSessionToken = useCallback(() => {
    const g = window.google
    if (g?.maps?.places?.AutocompleteSessionToken) {
      sessionTokRef.current = new g.maps.places.AutocompleteSessionToken()
    }
  }, [])

  const fetchPredictionsFromBackend = useCallback((value: string) => {
    const seq = ++reqSeqRef.current
    setAcLoading(true)
    businessApi
      .search(value, '')
      .then(({ candidates }) => {
        if (seq !== reqSeqRef.current) return
        setAcLoading(false)
        if (candidates?.length) {
          setPredictions(
            candidates.slice(0, 6).map((c) => ({
              placeId: c.external_id,
              mainText: c.name,
              secondaryText: c.address,
              city: c.city,
            }))
          )
          setShowDropdown(true)
        } else {
          setPredictions([])
          setShowDropdown(false)
        }
      })
      .catch(() => {
        if (seq !== reqSeqRef.current) return
        setAcLoading(false)
        setPredictions([])
        setShowDropdown(false)
      })
  }, [])

  const fetchPredictions = useCallback((value: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current)

    if (value.length < 2) {
      setPredictions([])
      setShowDropdown(false)
      return
    }

    debounceRef.current = setTimeout(() => {
      if (!autoSvcRef.current) {
        fetchPredictionsFromBackend(value)
        return
      }

      setAcLoading(true)
      autoSvcRef.current.getPlacePredictions(
        {
          input: value,
          types: ['establishment'],
          sessionToken: sessionTokRef.current,
        },
        (results: GooglePlacePrediction[] | null, status: string) => {
          setAcLoading(false)
          if (status === 'OK' && results?.length) {
            setPredictions(
              results.map((r) => ({
                placeId: r.place_id,
                mainText: r.structured_formatting?.main_text ?? r.description ?? '',
                secondaryText: r.structured_formatting?.secondary_text ?? '',
              }))
            )
            setShowDropdown(true)
          } else {
            fetchPredictionsFromBackend(value)
          }
        }
      )
    }, 300)
  }, [fetchPredictionsFromBackend])

  const handleQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setQuery(value)
    setSelectedPlaceId('')
    fetchPredictions(value)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') setShowDropdown(false)
  }

  const handleSelectPrediction = (pred: PlacePrediction) => {
    const extractedCity = pred.city || extractCityFromSecondary(pred.secondaryText)
    setQuery(pred.mainText)
    setCity(extractedCity)
    setSelectedPlaceId(pred.placeId)
    setShowDropdown(false)
    setPredictions([])
    refreshSessionToken()
    const params = new URLSearchParams({ q: pred.mainText, city: extractedCity })
    if (pred.placeId) params.set('place_id', pred.placeId)
    navigate(`/search?${params.toString()}`)
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (!query.trim()) return
    setShowDropdown(false)
    const params = new URLSearchParams({ q: query, city })
    if (selectedPlaceId) params.set('place_id', selectedPlaceId)
    navigate(`/search?${params.toString()}`)
  }

  const features = [
    {
      icon: Search,
      title: t('features.search.title'),
      desc: t('features.search.desc'),
    },
    {
      icon: Star,
      title: t('features.experience.title'),
      desc: t('features.experience.desc'),
    },
    {
      icon: Globe,
      title: t('features.listings.title'),
      desc: t('features.listings.desc'),
    },
  ]

  const platformItems = [
    { icon: MessageSquare, label: t('platform.items.whatsapp') },
    { icon: Mail, label: t('platform.items.email') },
    { icon: Phone, label: t('platform.items.sms') },
    { icon: Globe, label: t('platform.items.ordering') },
    { icon: Star, label: t('platform.items.reviews') },
    { icon: Search, label: t('platform.items.seo') },
  ]

  const trustItems = [
    t('trust.items.score'),
    t('trust.items.competitors'),
    t('trust.items.ai'),
    t('trust.items.website'),
    t('trust.items.reviews'),
    t('trust.items.profile'),
  ]

  const footerYear = new Date().getFullYear()

  // Figma canvas widths (1920px artboard)
  const shellHeader = 'max-w-[1070px] mx-auto w-full px-5 sm:px-6'
  const shellHero = 'max-w-[1311px] mx-auto w-full px-5 sm:px-6'
  const shellMain = 'max-w-[1193px] mx-auto w-full px-5 sm:px-6'
  const shellSearch = 'max-w-[848px] mx-auto w-full'
  const shellFooter = 'max-w-[1320px] mx-auto w-full px-5 sm:px-6 lg:px-8'

  return (
    <div className="min-h-screen bg-[#f2f2f6]">
      {/* Header */}
      <header className="bg-white sticky top-0 z-50">
        <div className={`${shellHeader} min-h-[100px] py-4 flex items-center justify-between gap-4`}>
          <CartleyLogo />

          <nav className="flex items-center justify-end gap-1.5 sm:gap-x-4">
            <LanguageSwitcher variant="compact" />
            <a
              href={CONSULTATION_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={tCommon('nav.book')}
              className="inline-flex h-10 w-10 items-center justify-center gap-1.5 rounded-[10px] px-0 text-xs font-medium text-white transition-colors whitespace-nowrap md:h-[34px] md:w-auto md:px-5 md:text-sm"
              style={{ backgroundColor: BRAND.red }}
              onMouseOver={(e) => { e.currentTarget.style.backgroundColor = BRAND.redHover }}
              onMouseOut={(e) => { e.currentTarget.style.backgroundColor = BRAND.red }}
            >
              <Calendar className="h-3.5 w-3.5 shrink-0" />
              <span className="hidden md:inline">{tCommon('nav.book')}</span>
            </a>
            {isAuthenticated ? (
              <button
                onClick={() => navigate('/dashboard')}
                aria-label={tCommon('nav.dashboard')}
                className="inline-flex h-10 w-10 items-center justify-center gap-1.5 rounded-[10px] border border-[#01114f] bg-white px-0 text-xs font-medium text-[#01114f] transition-colors hover:bg-[#f2f2f6] whitespace-nowrap md:h-[34px] md:w-auto md:px-4 md:text-sm"
              >
                <LayoutDashboard className="h-3.5 w-3.5 shrink-0" />
                <span className="hidden md:inline">{tCommon('nav.dashboard')}</span>
              </button>
            ) : (
              <button
                onClick={() => navigate('/login')}
                aria-label={tCommon('nav.login')}
                className="inline-flex h-10 w-10 items-center justify-center gap-1.5 rounded-[10px] border border-[#01114f] bg-white px-0 text-xs font-medium text-[#01114f] transition-colors hover:bg-[#f2f2f6] whitespace-nowrap md:h-[34px] md:w-auto md:px-4 md:text-sm"
              >
                <LogIn className="h-3.5 w-3.5 shrink-0" />
                <span className="hidden md:inline">{tCommon('nav.login')}</span>
              </button>
            )}
          </nav>
        </div>
      </header>

      <main>
        {/* Hero */}
        <section className={`${shellHero} pt-10 sm:pt-12 lg:pt-[38px] pb-12 lg:pb-14 text-center`}>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="inline-flex items-center gap-2 bg-white rounded-full h-8 px-4 mb-6 border border-gray-100">
              <CheckCircle className="w-3 h-3 shrink-0" style={{ color: BRAND.navy }} />
              <span className="text-xs font-medium" style={{ color: BRAND.navy }}>
                <span className="font-bold">{t('hero.badge.lead')}</span>
                {' '}
                {t('hero.badge.trail')}
              </span>
            </div>

            <h1 className="text-[28px] sm:text-4xl lg:text-[48px] font-bold leading-[1.25] lg:leading-[60px] mb-5 max-w-[1100px] mx-auto">
              <span style={{ color: BRAND.navy }}>{t('hero.title1')}</span>
              {' '}
              <span style={{ color: BRAND.red }}>{t('hero.titleHighlight')}</span>
              {' '}
              <span style={{ color: BRAND.navy }}>{t('hero.title2')}</span>
            </h1>

            <p className="text-base lg:text-lg text-[#6a7282] max-w-[1111px] mx-auto mb-3 leading-7">
              {t('hero.subtitle')}
            </p>
            <p className="text-sm font-medium mb-8" style={{ color: BRAND.navy }}>
              {t('hero.tagline', { appName: APP_NAME })}
            </p>

            <form
              ref={formRef}
              onSubmit={handleSearch}
              className={`${shellSearch} relative mb-8`}
            >
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-2 bg-white border border-[#f3f4f6] rounded-2xl p-3 sm:px-3 sm:py-0 sm:h-[66px] shadow-[0px_4px_6px_-1px_rgba(0,0,0,0.1),0px_2px_4px_-2px_rgba(0,0,0,0.1)]">
                <div className="flex items-center gap-2 flex-1 min-h-[40px] sm:min-h-0 px-1">
                  <Search className="w-4 h-4 text-[#99a1af] shrink-0" />
                  <input
                    type="text"
                    placeholder={t('hero.search.placeholder')}
                    value={query}
                    onChange={handleQueryChange}
                    onKeyDown={handleKeyDown}
                    onFocus={() => predictions.length > 0 && setShowDropdown(true)}
                    className="flex-1 text-sm text-gray-900 placeholder-[#99a1af] outline-none bg-transparent min-w-0"
                    autoFocus
                    autoComplete="off"
                    aria-autocomplete="list"
                    aria-expanded={showDropdown}
                    aria-haspopup="listbox"
                  />
                  {acLoading && (
                    <Loader2 className="w-3.5 h-3.5 text-[#99a1af] shrink-0 animate-spin" />
                  )}
                </div>
                <button
                  type="submit"
                  disabled={!query.trim()}
                  className="w-full sm:w-auto sm:shrink-0 disabled:bg-[#e5e7eb] disabled:text-[#99a1af] text-white font-medium text-sm px-5 h-10 rounded-xl transition-colors flex items-center gap-2 justify-center"
                  style={{ backgroundColor: query.trim() ? BRAND.red : undefined }}
                  onMouseOver={(e) => {
                    if (query.trim()) {
                      (e.currentTarget as HTMLButtonElement).style.backgroundColor = BRAND.redHover
                    }
                  }}
                  onMouseOut={(e) => {
                    if (query.trim()) {
                      (e.currentTarget as HTMLButtonElement).style.backgroundColor = BRAND.red
                    }
                  }}
                >
                  {t('hero.search.button')}
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>

              {showDropdown && predictions.length > 0 && (
                <div
                  role="listbox"
                  className="absolute top-full inset-s-0 inset-e-0 mt-2 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden z-50 text-start"
                >
                  {predictions.map((pred) => (
                    <button
                      key={pred.placeId}
                      role="option"
                      type="button"
                      onClick={() => handleSelectPrediction(pred)}
                      className="w-full flex items-start gap-3 px-4 py-3 hover:bg-gray-50 text-start transition-colors border-b border-gray-50 last:border-0"
                    >
                      <div className="w-7 h-7 bg-gray-100 rounded-lg flex items-center justify-center shrink-0 mt-0.5">
                        <MapPin className="w-3.5 h-3.5 text-gray-500" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{pred.mainText}</p>
                        {pred.secondaryText && (
                          <p className="text-xs text-gray-400 truncate mt-0.5">{pred.secondaryText}</p>
                        )}
                      </div>
                    </button>
                  ))}
                  <div className="px-4 py-2 flex justify-end">
                    <img
                      src="https://maps.gstatic.com/mapfiles/api-3/images/powered-by-google-on-white3_hdpi.png"
                      alt="Powered by Google"
                      className="h-4 opacity-60"
                    />
                  </div>
                </div>
              )}
            </form>

            <p className="text-sm font-medium max-w-[900px] mx-auto leading-5" style={{ color: BRAND.navy }}>
              {t('hero.trusted.prefix')}
              <span className="font-semibold" style={{ color: BRAND.red }}>
                {t('hero.trusted.count')}
              </span>
              {t('hero.trusted.suffix')}
            </p>
          </motion.div>
        </section>

        {/* Feature pillars */}
        <section className={`${shellMain} pb-8 lg:pb-10`}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-7">
            {features.map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 + 0.4 }}
                className="bg-white rounded-[14px] p-3.5 lg:p-4 min-h-[105px]"
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="bg-[#f9fafb] h-[30px] w-[30px] rounded-[10px] flex items-center justify-center shrink-0">
                    <item.icon className="w-5 h-5 text-gray-600" />
                  </div>
                  <h3 className="font-bold text-sm text-[#101828] leading-[22.4px]">{item.title}</h3>
                </div>
                <p className="text-sm text-[#6a7282] leading-5">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Divider */}
        <div className={shellMain}>
          <hr className="border-t border-gray-200" />
        </div>

        {/* Trust bar */}
        <section className="py-8 lg:py-10">
          <div className={`${shellMain} text-center`}>
            <p
              className="text-xs font-semibold uppercase tracking-[0.6px] mb-8"
              style={{ color: BRAND.navy }}
            >
              {t('trust.title')}
            </p>
            <ul className="flex flex-nowrap items-center justify-start lg:justify-between gap-x-6 lg:gap-x-3 w-full overflow-x-auto lg:overflow-visible pb-1 lg:pb-0 no-scrollbar">
              {trustItems.map((item) => (
                <li key={item} className="flex items-center gap-2 text-sm text-[#4a5565] shrink-0 whitespace-nowrap">
                  <CheckCircle className="w-4 h-4 text-green-600 shrink-0" strokeWidth={2} />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Cartley platform highlight */}
        <section className={`${shellMain} pb-12 lg:pb-16`}>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-[25px] px-5 sm:px-8 lg:px-10 py-8 lg:py-10 text-center min-h-[206px]"
          >
            <p
              className="text-xs font-bold uppercase tracking-[0.6px] mb-4"
              style={{ color: BRAND.red }}
            >
              {t('platform.badge', { appName: APP_NAME })}
            </p>
            <h2
              className="text-xl sm:text-2xl lg:text-[30px] font-bold leading-9 mb-3"
              style={{ color: BRAND.navy }}
            >
              {t('platform.title')}
            </h2>
            <p className="text-sm text-[#6a7282] max-w-[915px] mx-auto mb-8 lg:mb-10 leading-5">
              {t('platform.subtitle', { appName: APP_NAME })}
            </p>
            <div className="flex w-full flex-wrap items-center justify-center gap-2 lg:flex-nowrap lg:gap-2.5">
              {platformItems.map((item, index) => (
                <div
                  key={item.label}
                  className="inline-flex items-center gap-2 text-sm font-medium h-[38px] px-3 lg:px-3.5 rounded-full border shrink-0 whitespace-nowrap"
                  style={{
                    color: BRAND.navy,
                    borderColor: BRAND.navy,
                    backgroundColor: index === 0 ? 'rgba(1, 17, 79, 0.06)' : '#f0f1f4',
                  }}
                >
                  <item.icon className="w-4 h-4 shrink-0" style={{ color: BRAND.red }} />
                  {item.label}
                </div>
              ))}
            </div>
          </motion.div>
        </section>
      </main>

      {/* Footer */}
      <footer style={{ backgroundColor: BRAND.navy }}>
        <div className={`${shellFooter} pt-8 pb-6`}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 lg:gap-12">
            <div>
              <CartleyLogo variant="footer" />
              <address className="not-italic text-base text-white mt-6 leading-6">
                <p>{t('footer.address.line1')}</p>
                <p>{t('footer.address.line2')}</p>
              </address>
            </div>

            <div>
              <p className="text-base font-medium mb-4" style={{ color: BRAND.red }}>
                {t('footer.support')}
              </p>
              <ul className="space-y-3 text-base text-white">
                <li>
                  <button
                    type="button"
                    onClick={() => navigate('/login')}
                    className="hover:opacity-80 transition-opacity"
                  >
                    {tCommon('nav.getStarted')}
                  </button>
                </li>
                <li>
                  <a
                    href={CONSULTATION_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:opacity-80 transition-opacity"
                  >
                    {tCommon('nav.book')}
                  </a>
                </li>
                <li>
                  <a
                    href={FOOTER.contactUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:opacity-80 transition-opacity"
                  >
                    {t('footer.contact')}
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <p className="text-base font-medium mb-4" style={{ color: BRAND.red }}>
                {t('footer.follow')}
              </p>
              <div className="flex items-center gap-4">
                <a
                  href={FOOTER.instagramUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                  className="text-white hover:opacity-80 transition-opacity"
                >
                  <SocialIcon name="instagram" />
                </a>
                <a
                  href={FOOTER.facebookUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Facebook"
                  className="text-white hover:opacity-80 transition-opacity"
                >
                  <SocialIcon name="facebook" />
                </a>
                <a
                  href={FOOTER.youtubeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="YouTube"
                  className="text-white hover:opacity-80 transition-opacity"
                >
                  <SocialIcon name="youtube" />
                </a>
              </div>
            </div>
          </div>

          <hr className="border-t-2 border-white/30 my-8" />

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 text-base text-white">
            <p>
              {t('footer.copyright', { appName: APP_NAME, year: footerYear })}
            </p>
            <div className="flex flex-wrap gap-6 sm:justify-end">
              <a
                href={FOOTER.termsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:opacity-80 transition-opacity"
              >
                {t('footer.terms')}
              </a>
              <a
                href={FOOTER.privacyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:opacity-80 transition-opacity"
              >
                {t('footer.privacy')}
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}
